import APIV5 from '~/models/api';
import { UserModel } from '~/scripts/services/model/user';
import { Breakpoints, MD_AND_UP } from '~/services/breakpoints.service';
// eslint-disable-next-line import/no-cycle
import MfController from './mf.controller';

const mfInputDirective = /* @ngInject */ (
	/** @type {import('angular').IFilterService} */ $filter,
	/** @type {import('angular').IQService} */ $q,
	/** @type {import('angular').ITimeoutService} */ $timeout,
	/** @type {UserModel} */ User,
) => ({
	require:  [
		'ngModel',
		'^mf',
	],
	restrict: 'A',
	scope:    true,
	link:     (
		/** @type {any} */ scope,
		/** @type {any} */ element,
		/** @type {import('angular').IAttributes} */ attributes,
		/** @type {[import('angular').INgModelController, MfController]} */ ctrls,
	) => {
		/**
		 * Minimal number of characters that needs to be entered before MF kicks-in
		 *
		 * @type {number}
		 */
		const defaultMinLength = 2;

		/**
		 * Minimal wait time after last character typed before MF kicks-in
		 *
		 * @type {number}
		 */
		const waitTime = 400;

		const [ngModelCtrl, mfCtrl] = ctrls;

		let minLength = mfCtrl.options?.minLength ?? defaultMinLength;

		let input = '';

		let isMobile = false;

		/**
		 * Declare the timeout promise var outside the function scope so that stacked calls can be cancelled later
		 *
		 * @type {import('angular').IPromise<any>}
		 */
		let timeoutPromise;

		/**
		 * Generates matches array or prepare order for API
		 *
		 * @param {string} inputValue User input
		 */
		const getMatches = async inputValue => {
			mfCtrl.loading = true;
			mfCtrl.showSuggestion = false;
			mfCtrl.useKeyboard = false;
			mfCtrl.query = inputValue;

			if (mfCtrl.mfList?.length > 0) {
				mfCtrl.active.length = 0;
				mfCtrl.mapPath.length = 0;
				const matches = [];

				const results = $filter('filter')(mfCtrl.mfList, { name: inputValue });

				for (const [index, match] of results.entries()) {
					const m2 = {
						kid:        match.kid,
						name:       match.name,
						selectable: 1,
						model:      match,
						path:       [index],
					};
					matches.push(m2);
					mfCtrl.mapPath.push(m2.path);
				}
				mfCtrl.loading = false;

				mfCtrl.displayResults({ result: matches, status: 'OK' });
			}
			else if (mfCtrl.mfParse.enabled) {
				/** @type {any} */
				const locals = { $viewValue: inputValue };
				try {
					const m = await mfCtrl.mfParse.parseFromSource(locals);
					const { matches, parserResult } = m;
					// it might happen that several async queries were in progress if a user were typing fast
					// but we are interested only in responses that correspond to the current view value
					const onCurrentRequest = ngModelCtrl && inputValue === ngModelCtrl.$viewValue;
					if (onCurrentRequest && mfCtrl.hasFocus) {
						if (matches && matches.length > 0) {
							mfCtrl.active.length = 0;
							mfCtrl.mapPath.length = 0;
							mfCtrl.matches.length = 0;

							// transform labels
							for (const [index, match] of matches.entries()) {
								locals[parserResult.itemName] = match.name;
								const m2 = {
									kid:        match.kid,
									name:       parserResult.viewMapper(scope, locals) || match.name,
									selectable: 1,
									model:      match,
									path:       [index],
								};
								mfCtrl.matches.push(m2);
								mfCtrl.mapPath.push(m2.path);
							}

							mfCtrl.query = inputValue;
							// position pop-up with matches - we need to re-calculate its position each time we are opening a window
							// with matches as a pop-up might be absolute-positioned and position of an input might have changed on
							// a page due to other elements being rendered
							mfCtrl.recalculatePosition();

							element.attr('aria-expanded', true);

							// Select the single remaining option if user input matches
							// if (selectOnExact && mfCtrl.matches.length === 1 && inputIsExactMatch(inputValue, 0)) {
							// 	if (angular.isNumber(scope.debounceUpdate) || angular.isObject(scope.debounceUpdate)) {
							// 		$$debounce(() => {
							// 			mfCtrl.select(0, event);
							// 		}, angular.isNumber(scope.debounceUpdate) ? scope.debounceUpdate : scope.debounceUpdate.default);
							// 	}
							// 	else {
							// 		mfCtrl.select(0, event);
							// 	}
							// }
						}
						else {
							mfCtrl.resetMatches();
						}
					}
					if (onCurrentRequest) {
						mfCtrl.loading = false;
					}
				}
				catch {
					mfCtrl.resetMatches();
					mfCtrl.loading = false;
				}
			}
			else if (mfCtrl.hasFocus) {
				input = inputValue;
				const result = await APIV5.instance.autocomplete(
					{
						order: mfCtrl.order,
						query: input,
						...mfCtrl.apiOptions,
					},
					{ id: 'mf-input.get-matches' },
				);
				mfCtrl.loading = false;
				$timeout(() => {
					if (result && input === result.query) {
						mfCtrl.displayResults(result);
					}
				});
			}
		};

		const scheduleSearchWithTimeout = (/** @type {string} */ inputValue) => {
			timeoutPromise = $timeout(() => getMatches(inputValue), waitTime);
		};

		const cancelPreviousTimeout = () => {
			if (timeoutPromise) {
				$timeout.cancel(timeoutPromise);
			}
		};

		const mdAndUpBP = Breakpoints.create(MD_AND_UP);
		isMobile = mdAndUpBP.matches === false;
		mdAndUpBP.addEventListener('change', mql => {
			isMobile = mql.matches === false;
		});

		attributes.$observe('disabled', newValue => {
			const mfSuggestionsButton = element.parent().find('.mf-suggestions');
			if (mfSuggestionsButton.length > 0) {
				mfSuggestionsButton.prop('disabled', newValue);
			}
		});

		if (scope.component === undefined) {
			scope.component = {
				key: `K${User.getRef()}`,
			};
		}

		if (ngModelCtrl) {
			if (ctrls[1].noDirtyCheck || ctrls[1].card.to > 1) {
				ngModelCtrl.$setDirty = () => {};
			}

			// plug into $parsers pipeline to open a MF on view changes initiated from DOM
			// $parsers kick-in on all the changes coming from the view as well as manually triggered by $setViewValue
			ngModelCtrl.$parsers.unshift(inputValue => {
				// mfCtrl.hasFocus = true;

				// here we set minLength to 1 if the input is a number, so the mf can request on single numbers
				// and if its not a not a number, the minLength stay (or go back) to the default min length (const)
				minLength = inputValue.length < minLength && /^\d/.test(inputValue) ? 1 : minLength;

				if (mfCtrl.hasFocus && (minLength === 0 || (inputValue && inputValue.length >= minLength))) {
					if (waitTime > 0) {
						cancelPreviousTimeout();
						scheduleSearchWithTimeout(inputValue);
					}
					else {
						(async () => getMatches(inputValue))();
					}
				}
				else {
					mfCtrl.query = '';
					mfCtrl.loading = false;
					cancelPreviousTimeout();
					mfCtrl.resetMatches();
				}

				return inputValue;
			});

			ngModelCtrl.$formatters.push(modelValue => {
				/** @type {any} */
				const locals = {};

				if (!mfCtrl.parserResult) {
					return modelValue;
				}

				// if (inputFormatter) {
				// 	locals.$model = modelValue;
				// 	return inputFormatter(originalScope, locals);
				// }

				// it might happen that we don't have enough info to properly render input value
				// we need to check for this situation and simply return model value if we can't apply custom formatting
				locals[mfCtrl.parserResult.itemName] = modelValue;
				const candidateViewValue = mfCtrl.parserResult.viewMapper(mfCtrl.$scope, locals);
				locals[mfCtrl.parserResult.itemName] = undefined;
				const emptyViewValue = mfCtrl.parserResult.viewMapper(mfCtrl.$scope, locals);

				return candidateViewValue === emptyViewValue ? modelValue : candidateViewValue;
			});
		}

		element.on('focus', (/** @type {Event} */ event) => {
			mfCtrl.hasFocus = true;
			if (minLength === 0 && mfCtrl.ngModelCtrl && !mfCtrl.ngModelCtrl.$viewValue) {
				$timeout(() => getMatches(mfCtrl?.ngModelCtrl?.$viewValue), 0);
			}
			if (event?.target && !isMobile) {
				// @ts-ignore
				event.target.select();
			}
			mfCtrl.mapIndex = -1;

			if (mfCtrl.oldMatches && mfCtrl.oldMapPath) {
				mfCtrl.matches = mfCtrl.oldMatches;
				mfCtrl.mapPath.push(...mfCtrl.oldMapPath);
				mfCtrl.active.length = 0;
				if (mfCtrl.ngModelCtrl) {
					$timeout(() => {
						scope.$apply(() => {
							// Reset validity as we are clearing
							mfCtrl.ngModelCtrl?.$setValidity('editable', true);
							mfCtrl.ngModelCtrl?.$setValidity('parse', true);
						});
					});
				}
			}
		});

		element.on('change', (/** @type {Event} */ event) => {
			event.preventDefault();
			event.stopImmediatePropagation();
		});

		element.on('blur', () => {
			if (!mfCtrl.useKeyboard) {
				if (
					!mfCtrl.loading
					&& (
						(mfCtrl.uniqueKid && !mfCtrl.values)
						|| (!mfCtrl.uniqueKid && Array.isArray(mfCtrl.values) && mfCtrl.values.length === 0)
						|| mfCtrl.displayValues
					)
					&& (
						(mfCtrl.matches.length === 0 && mfCtrl.card.to === 1)
						|| (mfCtrl.matches.length === 1 && mfCtrl.matches[0].kid === '__none')
					)
				) {
					mfCtrl.mfInput = undefined;
					ngModelCtrl.$setViewValue('');
				}

				if (mfCtrl.ngModelCtrl && mfCtrl.ngModelCtrl.$error.editable) {
					mfCtrl.ngModelCtrl?.$setViewValue(undefined);
					scope.$apply(() => {
						// Reset validity as we are clearing
						mfCtrl.ngModelCtrl?.$setValidity('editable', true);
						mfCtrl.ngModelCtrl?.$setValidity('parse', true);
					});
				}
				mfCtrl.hasFocus = false;
			}
		});

		element.on('keyup copy paste cut', (/** @type {Event} */ event) => {
			if (
				mfCtrl.card.to === 1
				&& (
					(mfCtrl.uniqueKid && mfCtrl.values)
					|| (!mfCtrl.uniqueKid && Array.isArray(mfCtrl.values) && mfCtrl.values.length === 1)
				)
				// @ts-ignore
				&& mfCtrl.values[0].name !== event.target?.value
			) {
				mfCtrl.disableAutoUpdate = true;
				if (mfCtrl.mfInput?.length === 0) {
					mfCtrl.delValue(0, event);
				}
				// @ts-ignore
				if (event.target?.value === '') {
					mfCtrl.setModelValue(
						mfCtrl.options && 'linkerKid' in mfCtrl.options && mfCtrl.options.linkerKid === true
							? '0' : [],
					);
				}
			}
		});

		scope.$on('$destroy', () => {
			element.off('focus blur keyup copy paste cut');
		});
	},
});

export default mfInputDirective;
